import { omit } from 'lodash';

import { Action } from '../../actions';
import { ON_ACCEPT_MASS_ACTIONS_BANNERS_PRICE } from '../../actions/banners-edit/banners-edit-mass-actions';
import {
    OPEN_BANNER_PRICE_EDITOR,
    OpenBannerPriceEditor,
    RESET_BANNER_DRAFT_PRICE
} from '../../actions/banners-edit/price';
import {
    CLOSE_BANNERS_PRICES_POPUP,
    OPEN_BANNERS_PRICES_POPUP,
    RESET_BANNERS_PRICES,
    SWITCH_BANNERS_PRICES_POPUP_FORM,
    UPDATE_BANNERS_PRICES_POPUP,
    UPDATE_BANNERS_PRICES_POPUP_ERRORS,
    VALIDATE_BANNERS_PRICES
} from '../../actions/banners-prices';
import {
    DELETE_BANNERS,
    RESET_BANNERS_EDIT,
    SAVE_BANNERS_SUCCESS,
    SaveBannersSuccess
} from '../../actions/bannersEdit';
import { selectBannersMassActionsWorkCurrency } from '../../selectors/bannerEdit/massActions/massActions';
import { getDefaultBannerPriceData } from '../../service/BannerHelpers/BannerHelpers';
import {
    EditorBannerPriceData,
    EditorBannerPriceState,
    getEditedBannerWithPrice,
    MassActionId
} from '../../typings/bannersEditState';
import { EditorPlaceId } from '../../typings/common';
import { CurrencyCode } from '../../typings/graphql-grid';
import { State } from '../../typings/state';

const getDefaultPriceData = (): EditorBannerPriceData => ({
    bannerPrice: getDefaultBannerPriceData(CurrencyCode.RUB),
    isVisible: false,
    isOnProgress: false,
    isPriceFormVisible: false,
    errors: []
});

const initialState = {
    [MassActionId.bannersEdit]: getDefaultPriceData(),
    [MassActionId.bannerGrid]: getDefaultPriceData()
};

const initPriceData = (
    state: EditorBannerPriceState,
    action: OpenBannerPriceEditor,
    fullState: State
): EditorBannerPriceState => {
    const banner = getEditedBannerWithPrice(
        fullState.editBanners.banners[action.bannerId]
    );
    if (!banner) {
        return state;
    }

    return {
        ...state,
        [action.bannerId]: {
            ...state[action.bannerId],
            bannerPrice:
                banner.bannerPriceEditor.bannerPrice ||
                getDefaultBannerPriceData(
                    selectBannersMassActionsWorkCurrency(fullState)
                )
        }
    };
};

const deletePrices = (
    state: EditorBannerPriceState,
    ids: EditorPlaceId[]
): EditorBannerPriceState => {
    const newState = omit(state, ids);

    return newState;
};

const setSavedPrices = (
    state: EditorBannerPriceState,
    action: SaveBannersSuccess
): EditorBannerPriceState => {
    const { added } = action.result;
    const pricesData = state;

    if (added.banners.length) {
        added.banners.forEach((addedBanner, index) => {
            if (!addedBanner) {
                return;
            }
            const oldId = added.ids[index];
            const newId = String(addedBanner.id);

            pricesData[newId] = {
                ...pricesData[oldId]
            };

            delete pricesData[oldId];
        });

        state = pricesData;
    }

    return state;
};

export const pricesReducer = (
    state: EditorBannerPriceState = initialState,
    action: Action,
    fullState: State
): EditorBannerPriceState => {
    switch (action.type) {
        case RESET_BANNERS_EDIT:
            return initialState;
        case OPEN_BANNER_PRICE_EDITOR:
            return initPriceData(state, action, fullState);
        case ON_ACCEPT_MASS_ACTIONS_BANNERS_PRICE:
        case CLOSE_BANNERS_PRICES_POPUP: {
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    isVisible: false,
                    errors: [],
                    isOnProgress: false
                }
            };
        }

        case SWITCH_BANNERS_PRICES_POPUP_FORM: {
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    isPriceFormVisible: !state[action.bannerId]
                        .isPriceFormVisible,
                    isOnProgress: false,
                    errors: []
                }
            };
        }
        case OPEN_BANNERS_PRICES_POPUP: {
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    isVisible: true,
                    isOnProgress: false,
                    errors: [],
                    bannerPrice: getDefaultBannerPriceData(
                        selectBannersMassActionsWorkCurrency(fullState)
                    ),
                    isPriceFormVisible: true
                }
            };
        }

        case UPDATE_BANNERS_PRICES_POPUP_ERRORS: {
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    errors: action.errors,
                    isOnProgress: false
                }
            };
        }

        case RESET_BANNERS_PRICES: {
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    bannerPrice: getDefaultBannerPriceData(
                        selectBannersMassActionsWorkCurrency(fullState)
                    ),
                    errors: [],
                    isOnProgress: true
                }
            };
        }

        case RESET_BANNER_DRAFT_PRICE: {
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    bannerPrice: getDefaultBannerPriceData(
                        selectBannersMassActionsWorkCurrency(fullState)
                    ),
                    errors: [],
                    isOnProgress: true
                }
            };
        }

        case VALIDATE_BANNERS_PRICES: {
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    isOnProgress: true,
                    errors: []
                }
            };
        }

        case UPDATE_BANNERS_PRICES_POPUP: {
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    bannerPrice: {
                        ...state[action.bannerId].bannerPrice,
                        [action.changed.field]: action.changed.value
                    },
                    isVisible: true,
                    errors: (state[action.bannerId].errors || []).filter(
                        error => {
                            // ошибки error.path = '', удаляются всегда
                            if (!error.path) {
                                return false;
                            }

                            return action.paths
                                ? !action.paths.includes(error.path)
                                : true;
                        }
                    )
                }
            };
        }
        case DELETE_BANNERS:
            return deletePrices(state, action.bannersIds);
        case SAVE_BANNERS_SUCCESS:
            return setSavedPrices(state, action);
        default:
            return state;
    }
};
